import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Grid, DialogContent, DialogActions,
  TextField, Alert, Tooltip, IconButton, InputAdornment,
  List, ListItem, ListItemText, ListItemButton, CircularProgress,
  useTheme, Autocomplete, Divider, Collapse,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
  Star, Refresh, Add, Delete, SwapVert, ArrowUpward, ArrowDownward,
  Search, LocationOn, Business, ExpandMore, ExpandLess, Map,
} from '@mui/icons-material';
import api from '../../utils/api';
import { INDIAN_CITIES } from '../../utils/indianCities';
import {
  SectionHeader, GlassPaper, GlassDialog,
  GradientButton, EmptyState,
} from './ui';
import { glass } from './ui/glassTokens';

// ─── constants ────────────────────────────────────────────────────────────────
const MAX_SLOTS = 3;
const EMPTY_SLOTS = Array.from({ length: MAX_SLOTS }, () => null);

// Sentinel value for the city-wide option (empty string = omit locality param)
const CITY_WIDE_VALUE = '';
const CITY_WIDE_LABEL = 'City-wide (all areas)';

// ─── helpers ──────────────────────────────────────────────────────────────────
// URL-safe path segment for the admin endpoints. Backend is the source of truth
// for canonical city normalization (see backend/src/utils/cityClusters.js).
function cityPath(city) {
  return city ? encodeURIComponent(city.trim()) : '';
}

// Case-insensitive equality on city display names — used to match the
// backend's canonical c.city_normalized against the user's selectedCity.
function sameCity(a, b) {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

// Returns a full query string (starting with '?') for endpoints where locality
// is the first (and possibly only) param.
function localityQuery(locality) {
  if (!locality) return '';
  return `?locality=${encodeURIComponent(locality)}`;
}

// Human-readable label for a locality_normalized value in the left panel.
function localityLabel(value, label) {
  if (!value) return CITY_WIDE_LABEL;
  return label || value.replace(/_/g, ' ');
}

// ─── SlotCard ─────────────────────────────────────────────────────────────────
function SlotCard({
  index, slot, onPick, onRemove, onMoveUp, onMoveDown,
  canMoveUp, canMoveDown, disabled,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const g = glass(theme);

  const labelColor = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)';
  const textColor = isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)';
  const subColor = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)';

  return (
    <Box
      sx={{
        ...g.surface,
        borderRadius: 2,
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        minHeight: 80,
      }}
    >
      {/* Position badge */}
      <Box
        sx={{
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: '10px',
          background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontWeight: 700,
          fontSize: '0.9rem',
        }}
        aria-label={`Slot ${index + 1}`}
      >
        {index + 1}
      </Box>

      {/* Library info or empty prompt */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {slot ? (
          <>
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap
              sx={{ color: textColor }}
            >
              {slot.name}
            </Typography>
            {slot.address && (
              <Typography
                variant="caption"
                noWrap
                sx={{ color: subColor, display: 'block' }}
              >
                {slot.address}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" sx={{ color: labelColor, fontStyle: 'italic' }}>
            Empty slot — click to assign a library
          </Typography>
        )}
      </Box>

      {/* Actions */}
      <Box sx={{ flexShrink: 0, display: 'flex', gap: 0.5 }}>
        {/* Move up */}
        <Tooltip title="Move up">
          <span>
            <IconButton
              size="small"
              onClick={() => onMoveUp(index)}
              disabled={!canMoveUp || disabled}
              aria-label={`Move slot ${index + 1} up`}
              sx={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}
            >
              <ArrowUpward fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        {/* Move down */}
        <Tooltip title="Move down">
          <span>
            <IconButton
              size="small"
              onClick={() => onMoveDown(index)}
              disabled={!canMoveDown || disabled}
              aria-label={`Move slot ${index + 1} down`}
              sx={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}
            >
              <ArrowDownward fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        {/* Pick / Replace */}
        <Tooltip title={slot ? 'Replace library' : 'Pick library'}>
          <span>
            <IconButton
              size="small"
              onClick={() => onPick(index)}
              disabled={disabled}
              aria-label={slot ? `Replace slot ${index + 1}` : `Pick library for slot ${index + 1}`}
              sx={{ color: '#6366F1' }}
            >
              {slot ? <SwapVert fontSize="small" /> : <Add fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>

        {/* Remove */}
        {slot && (
          <Tooltip title="Remove">
            <IconButton
              size="small"
              onClick={() => onRemove(index)}
              disabled={disabled}
              aria-label={`Remove slot ${index + 1}`}
              sx={{ color: '#F43F5E' }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

// Normalize locality for comparison against backend-stored values.
// Rule: trim, collapse whitespace, lowercase — mirrors the backend normalizer.
function normalizeLocality(s) {
  return String(s || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function FeaturedLibrariesManagement({ apiBase = '/admin' }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const g = glass(theme);

  // ── state ──
  const [cityList, setCityList] = useState([]);       // cities with existing curation
  const [selectedCity, setSelectedCity] = useState(null);

  // Locality state
  const [localities, setLocalities] = useState([]);              // [{value, label, count}]
  const [selectedLocality, setSelectedLocality] = useState(CITY_WIDE_VALUE);
  const [localitiesLoading, setLocalitiesLoading] = useState(false);

  const [slots, setSlots] = useState([...EMPTY_SLOTS]);
  const [candidates, setCandidates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSlotIndex, setPickerSlotIndex] = useState(null);
  const [confirmCityMismatch, setConfirmCityMismatch] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [mismatchCity, setMismatchCity] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState('');

  // ── connected marketing page state ──
  const [pages, setPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [linkingPageId, setLinkingPageId] = useState(null);
  const [pickedPage, setPickedPage] = useState(null);

  // Picker scope: 'all' searches across all active libraries; 'city' restricts to selectedCity
  const [pickerScope, setPickerScope] = useState('all');

  // Left-panel expand state: tracks which city_normalized entries are expanded
  const [expandedCities, setExpandedCities] = useState({});

  const searchDebounceRef = useRef(null);

  // ── shared text colours ──
  const labelColor = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)';
  const textColor = isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)';
  const subColor = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)';

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      ...g.surface,
      '& fieldset': { border: 'none' },
      color: textColor,
    },
    '& .MuiInputLabel-root': { color: labelColor },
    '& .MuiInputBase-input': { color: textColor },
  };

  // Disabled variant — slightly more muted, pointer-events blocked
  const inputSxDisabled = {
    ...inputSx,
    opacity: 0.55,
    pointerEvents: 'none',
  };

  // ── locality options for the Autocomplete ──
  const localityOptions = [
    { value: CITY_WIDE_VALUE, label: CITY_WIDE_LABEL },
    ...localities.map((l) => ({
      value: l.value,
      label: `${l.label} (${l.count})`,
    })),
  ];

  // ── load city list on mount ──
  useEffect(() => {
    fetchCityList();
  }, []);

  const fetchCityList = async () => {
    try {
      setLoading(true);
      const res = await api.get(`${apiBase}/featured-libraries`);
      setCityList(res.data.data.cities || []);
    } catch (err) {
      setError('Failed to load featured library data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ── fetch published SEO pages for the marketing-page link card ──
  const fetchPages = useCallback(async (search) => {
    try {
      setPagesLoading(true);
      const params = search ? { search } : {};
      const res = await api.get(`${apiBase}/featured-libraries/pages`, { params });
      setPages(res.data.data.pages || []);
    } catch (err) {
      console.error('Pages fetch failed', err);
      setPages([]);
    } finally {
      setPagesLoading(false);
    }
  }, []);

  // Fetch pages whenever a city is first chosen
  useEffect(() => {
    if (selectedCity) {
      fetchPages();
    } else {
      setPages([]);
      setPickedPage(null);
    }
  }, [selectedCity, fetchPages]);

  // ── fetch localities for a city, then reset selected locality ──
  const fetchLocalities = useCallback(async (city) => {
    if (!city) {
      setLocalities([]);
      setSelectedLocality(CITY_WIDE_VALUE);
      return;
    }
    try {
      setLocalitiesLoading(true);
      const res = await api.get(`${apiBase}/featured-libraries/localities/${cityPath(city)}`);
      setLocalities(res.data.data.localities || []);
    } catch (err) {
      // Non-fatal: city may simply have no locality data
      console.error('Localities fetch failed', err);
      setLocalities([]);
    } finally {
      setLocalitiesLoading(false);
      setSelectedLocality(CITY_WIDE_VALUE);
    }
  }, []);

  // ── fetch candidates (shared by loadCity, handleSearchChange, and picker scope toggle) ──
  // scope: 'all' | 'city'. When 'all', locality param is omitted regardless of selectedLocality.
  const fetchCandidates = useCallback(async (city, search, scope, locality) => {
    if (!city) return;
    try {
      setCandidatesLoading(true);
      const params = { limit: 50 };
      if (search) params.search = search;
      params.scope = scope;
      if (scope === 'city' && locality) params.locality = locality;
      const res = await api.get(
        `${apiBase}/featured-libraries/candidates/${cityPath(city)}`,
        { params }
      );
      setCandidates(res.data.data.libraries || []);
    } catch (err) {
      console.error('Candidate fetch failed', err);
    } finally {
      setCandidatesLoading(false);
    }
  }, []);

  // ── load a city+locality's current featured + candidates ──
  const loadCity = useCallback(async (city, locality, scope) => {
    if (!city) return;
    setError('');
    const loc = locality !== undefined ? locality : CITY_WIDE_VALUE;
    const effectiveScope = scope !== undefined ? scope : 'all';
    try {
      setLoading(true);
      const cityNorm = cityPath(city);
      const locQ = localityQuery(loc);

      const featuredRes = await api.get(`${apiBase}/featured-libraries/${cityNorm}${locQ}`);

      const featuredLibs = featuredRes.data.data.libraries || [];
      const newSlots = [...EMPTY_SLOTS];
      featuredLibs.forEach((lib) => {
        const pos = lib.position - 1; // API is 1-indexed
        if (pos >= 0 && pos < MAX_SLOTS) {
          newSlots[pos] = {
            library_id: lib.library_id,
            name: lib.name,
            address: lib.address,
          };
        }
      });
      setSlots(newSlots);
    } catch (err) {
      setError('Failed to load city data');
      console.error(err);
    } finally {
      setLoading(false);
    }
    // Fetch candidates separately so the spinner is independent
    fetchCandidates(city, '', effectiveScope, loc);
  }, [fetchCandidates]);

  const handleCityChange = (newCity) => {
    setSelectedCity(newCity);
    setSlots([...EMPTY_SLOTS]);
    setCandidates([]);
    setSearchTerm('');
    setLocalities([]);
    setSelectedLocality(CITY_WIDE_VALUE);
    if (newCity) {
      // Fetch localities first; loadCity will be triggered once locality resets
      fetchLocalities(newCity).then(() => {
        // After fetchLocalities resolves, locality is reset to CITY_WIDE_VALUE
        loadCity(newCity, CITY_WIDE_VALUE, pickerScope);
      });
    }
  };

  const handleLocalityChange = (newLocality) => {
    setSelectedLocality(newLocality);
    setSlots([...EMPTY_SLOTS]);
    setCandidates([]);
    setSearchTerm('');
    loadCity(selectedCity, newLocality, pickerScope);
  };

  // ── candidate search (debounced 300ms) ──
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      if (!selectedCity) return;
      fetchCandidates(selectedCity, val, pickerScope, selectedLocality);
    }, 300);
  };

  // ── scope toggle handler (inside picker) ──
  const handlePickerScopeChange = (_, newScope) => {
    if (!newScope || newScope === pickerScope) return;
    setPickerScope(newScope);
    // Re-fetch immediately with the new scope and current search term
    if (selectedCity) {
      fetchCandidates(
        selectedCity,
        searchTerm,
        newScope,
        newScope === 'city' ? selectedLocality : CITY_WIDE_VALUE
      );
    }
  };

  // ── slot actions ──
  const openPicker = (slotIndex) => {
    setPickerSlotIndex(slotIndex);
    setSearchTerm('');
    setPickerOpen(true);
    // Always open with all-libraries scope and fetch fresh candidates
    setPickerScope('all');
    if (selectedCity) {
      fetchCandidates(selectedCity, '', 'all', CITY_WIDE_VALUE);
    }
  };

  const assignLibrary = (library) => {
    const newSlots = [...slots];
    newSlots[pickerSlotIndex] = {
      library_id: library.id,
      name: library.name,
      address: library.address,
    };
    setSlots(newSlots);
    setPickerOpen(false);
  };

  const removeSlot = (index) => {
    const newSlots = [...slots];
    newSlots[index] = null;
    setSlots(newSlots);
  };

  const moveSlot = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= MAX_SLOTS) return;
    const newSlots = [...slots];
    [newSlots[index], newSlots[target]] = [newSlots[target], newSlots[index]];
    setSlots(newSlots);
  };

  // libraries already assigned to a slot — to disable them in the picker
  const assignedIds = new Set(
    slots.filter(Boolean).map((s) => s.library_id)
  );

  // ── marketing page link / unlink ──
  const handleLinkPage = async () => {
    if (!pickedPage || !selectedCity) return;
    setLinkingPageId(pickedPage.id);
    try {
      await api.put(`${apiBase}/featured-libraries/pages/${pickedPage.id}/link`, {
        city: selectedCity,
        locality: selectedLocality,
      });
      setSnackbar({ open: true, message: 'Page linked successfully', severity: 'success' });
      setPickedPage(null);
      fetchPages();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to link page',
        severity: 'error',
      });
    } finally {
      setLinkingPageId(null);
    }
  };

  const handleUnlinkPage = async (pageId) => {
    setLinkingPageId(pageId);
    try {
      await api.delete(`${apiBase}/featured-libraries/pages/${pageId}/link`);
      setSnackbar({ open: true, message: 'Page unlinked', severity: 'info' });
      fetchPages();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to unlink page',
        severity: 'error',
      });
    } finally {
      setLinkingPageId(null);
    }
  };

  // Find the page currently linked to the active (selectedCity, selectedLocality) set.
  // featured_city is normalized on the backend; use sameCity (case-insensitive) to match.
  // featured_locality is already normalized; selectedLocality is already normalized.
  const linkedPage = pages.find(
    (p) =>
      sameCity(p.featured_city, selectedCity) &&
      normalizeLocality(p.featured_locality) === normalizeLocality(selectedLocality)
  ) || null;

  // ── save ──
  const handleSave = async (force = false) => {
    if (!selectedCity) return;
    setSaving(true);
    setError('');
    try {
      const payload = slots
        .map((slot, i) => slot ? { library_id: slot.library_id, position: i + 1 } : null)
        .filter(Boolean);

      // Build URL: locality goes first, force appended with & if needed
      const locQ = localityQuery(selectedLocality);
      const forceParam = force ? (locQ ? '&force=true' : '?force=true') : '';
      const url = `${apiBase}/featured-libraries/${cityPath(selectedCity)}${locQ}${forceParam}`;
      await api.put(url, { libraries: payload });

      setSnackbar({ open: true, message: 'Featured libraries saved successfully', severity: 'success' });
      fetchCityList(); // refresh the city list panel
    } catch (err) {
      const code = err.response?.data?.code;
      const serverCity = err.response?.data?.data?.library_city;
      if (code === 'LIBRARY_CITY_MISMATCH') {
        setMismatchCity(serverCity || 'another city');
        setConfirmCityMismatch(true);
      } else {
        const msg = err.response?.data?.message || 'Failed to save featured libraries';
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleForceConfirm = async () => {
    setConfirmCityMismatch(false);
    await handleSave(true);
  };

  // ── clear ──
  const handleClear = async () => {
    if (!selectedCity) return;
    setConfirmClear(false);
    setClearing(true);
    setError('');
    try {
      const locQ = localityQuery(selectedLocality);
      await api.delete(`${apiBase}/featured-libraries/${cityPath(selectedCity)}${locQ}`);
      setSlots([...EMPTY_SLOTS]);
      setSnackbar({ open: true, message: 'Featured libraries cleared', severity: 'info' });
      fetchCityList();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to clear featured libraries');
    } finally {
      setClearing(false);
    }
  };

  const hasAnySlot = slots.some(Boolean);
  const isBusy = loading || saving || clearing;

  // ── left-panel city expand toggle ──
  const toggleCityExpand = (cityNorm) => {
    setExpandedCities((prev) => ({ ...prev, [cityNorm]: !prev[cityNorm] }));
  };

  // Determine if a (city, locality) pair is the currently selected one
  const isPairSelected = (cityNorm, locNorm) =>
    sameCity(selectedCity, cityNorm) && selectedLocality === locNorm;

  // ── click on a (city, locality) entry in the left panel ──
  const handlePanelEntryClick = (cityNorm, locNorm) => {
    const match = INDIAN_CITIES.find((ic) => sameCity(ic, cityNorm)) || cityNorm;
    // If same city but different locality, just switch locality
    if (sameCity(selectedCity, cityNorm)) {
      setSelectedLocality(locNorm);
      setSlots([...EMPTY_SLOTS]);
      setCandidates([]);
      setSearchTerm('');
      loadCity(match, locNorm, pickerScope);
    } else {
      // Different city: full reset, then load localities, then load slots
      setSelectedCity(match);
      setSlots([...EMPTY_SLOTS]);
      setCandidates([]);
      setSearchTerm('');
      setLocalities([]);
      setSelectedLocality(locNorm);
      fetchLocalities(match).then(() => {
        setSelectedLocality(locNorm);
        loadCity(match, locNorm, pickerScope);
      });
    }
  };

  // Slot-count label for a locality entry in the left panel
  const slotCountLabel = (libs) =>
    `${libs.length} / ${MAX_SLOTS} slots filled`;

  // Whether a city row in the panel should be auto-expanded (has >1 locality or is selected)
  const isCityExpanded = (cityNorm) => {
    if (expandedCities[cityNorm] !== undefined) return expandedCities[cityNorm];
    // Default: expand if this city is currently selected
    return sameCity(selectedCity, cityNorm);
  };

  // ── slot editor heading ──
  const slotHeading = selectedCity
    ? selectedLocality
      ? `Featured slots — ${selectedCity} · ${
          localities.find((l) => l.value === selectedLocality)?.label || selectedLocality
        }`
      : `Featured slots — ${selectedCity} (city-wide)`
    : '';

  return (
    <Box>
      <SectionHeader
        eyebrow="Library Management"
        title="Featured Libraries"
        description="Pin up to 3 libraries to the top of city or area searches"
        action={
          <GradientButton
            variant="ghost"
            startIcon={<Refresh />}
            onClick={fetchCityList}
            disabled={loading}
          >
            Refresh
          </GradientButton>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        >
          {snackbar.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left: curated cities list */}
        <Grid item xs={12} md={4}>
          <GlassPaper sx={{ p: 2, height: '100%' }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ color: textColor, mb: 1.5 }}
            >
              Cities with curation
            </Typography>

            {loading && cityList.length === 0 ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={28} />
              </Box>
            ) : cityList.length === 0 ? (
              <EmptyState
                icon={<Star />}
                iconIndex={0}
                title="No cities curated"
                description="Select a city below and assign featured libraries to get started."
              />
            ) : (
              <List dense disablePadding>
                {cityList.map((c) => {
                  const expanded = isCityExpanded(c.city_normalized);
                  const locEntries = c.localities || [];
                  // Show city-wide entry always at top; then per-locality entries
                  const cityWideEntry = locEntries.find((l) => l.locality_normalized === '') || null;
                  const areaEntries = locEntries.filter((l) => l.locality_normalized !== '');

                  return (
                    <Box key={c.city_normalized} sx={{ mb: 0.5 }}>
                      {/* City row (expand/collapse) */}
                      <ListItemButton
                        onClick={() => toggleCityExpand(c.city_normalized)}
                        sx={{
                          borderRadius: 1.5,
                          py: 0.75,
                          '&:hover': {
                            background: isDark
                              ? 'rgba(99,102,241,0.10)'
                              : 'rgba(99,102,241,0.06)',
                          },
                        }}
                        aria-expanded={expanded}
                        aria-label={`${c.city_normalized.replace(/_/g, ' ')} — toggle localities`}
                      >
                        <LocationOn
                          fontSize="small"
                          sx={{ mr: 1, color: '#6366F1', flexShrink: 0 }}
                        />
                        <ListItemText
                          primary={c.city_normalized.replace(/_/g, ' ')}
                          secondary={`${locEntries.length} area${locEntries.length !== 1 ? 's' : ''} curated`}
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontWeight: 700,
                            sx: { color: textColor, textTransform: 'capitalize' },
                          }}
                          secondaryTypographyProps={{
                            variant: 'caption',
                            sx: { color: subColor },
                          }}
                        />
                        {expanded ? (
                          <ExpandLess fontSize="small" sx={{ color: subColor }} />
                        ) : (
                          <ExpandMore fontSize="small" sx={{ color: subColor }} />
                        )}
                      </ListItemButton>

                      {/* Locality entries */}
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <List dense disablePadding sx={{ pl: 2 }}>
                          {/* City-wide entry first */}
                          {cityWideEntry && (
                            <ListItem key="__citywide" disablePadding>
                              <ListItemButton
                                selected={isPairSelected(c.city_normalized, '')}
                                onClick={() => handlePanelEntryClick(c.city_normalized, '')}
                                sx={{
                                  borderRadius: 1.5,
                                  mb: 0.5,
                                  '&.Mui-selected': {
                                    background: isDark
                                      ? 'rgba(99,102,241,0.18)'
                                      : 'rgba(99,102,241,0.10)',
                                    '&:hover': {
                                      background: isDark
                                        ? 'rgba(99,102,241,0.24)'
                                        : 'rgba(99,102,241,0.15)',
                                    },
                                  },
                                }}
                              >
                                <Map
                                  fontSize="small"
                                  sx={{ mr: 1, color: '#8B5CF6', flexShrink: 0 }}
                                />
                                <ListItemText
                                  primary={CITY_WIDE_LABEL}
                                  secondary={slotCountLabel(cityWideEntry.libraries)}
                                  primaryTypographyProps={{
                                    variant: 'body2',
                                    fontWeight: 600,
                                    sx: { color: textColor },
                                  }}
                                  secondaryTypographyProps={{
                                    variant: 'caption',
                                    sx: { color: subColor },
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          )}

                          {/* Per-area entries */}
                          {areaEntries.map((loc) => (
                            <ListItem key={loc.locality_normalized} disablePadding>
                              <ListItemButton
                                selected={isPairSelected(c.city_normalized, loc.locality_normalized)}
                                onClick={() =>
                                  handlePanelEntryClick(c.city_normalized, loc.locality_normalized)
                                }
                                sx={{
                                  borderRadius: 1.5,
                                  mb: 0.5,
                                  '&.Mui-selected': {
                                    background: isDark
                                      ? 'rgba(99,102,241,0.18)'
                                      : 'rgba(99,102,241,0.10)',
                                    '&:hover': {
                                      background: isDark
                                        ? 'rgba(99,102,241,0.24)'
                                        : 'rgba(99,102,241,0.15)',
                                    },
                                  },
                                }}
                              >
                                <Business
                                  fontSize="small"
                                  sx={{ mr: 1, color: '#6366F1', flexShrink: 0 }}
                                />
                                <ListItemText
                                  primary={localityLabel(
                                    loc.locality_normalized,
                                    loc.locality_normalized.replace(/_/g, ' ')
                                  )}
                                  secondary={slotCountLabel(loc.libraries)}
                                  primaryTypographyProps={{
                                    variant: 'body2',
                                    fontWeight: 600,
                                    sx: {
                                      color: textColor,
                                      textTransform: 'capitalize',
                                    },
                                  }}
                                  secondaryTypographyProps={{
                                    variant: 'caption',
                                    sx: { color: subColor },
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      </Collapse>
                    </Box>
                  );
                })}
              </List>
            )}
          </GlassPaper>
        </Grid>

        {/* Right: slot editor */}
        <Grid item xs={12} md={8}>
          <GlassPaper sx={{ p: 3 }}>
            {/* City picker */}
            <Box mb={2}>
              <Autocomplete
                freeSolo
                options={INDIAN_CITIES}
                value={selectedCity}
                filterOptions={(options, state) => {
                  const input = state.inputValue.trim();
                  const filtered = options.filter((opt) =>
                    opt.toLowerCase().includes(input.toLowerCase())
                  );
                  const hasExact = filtered.some(
                    (opt) => opt.toLowerCase() === input.toLowerCase()
                  );
                  if (input && !hasExact) {
                    return [{ inputValue: input, label: `Add "${input}"` }, ...filtered];
                  }
                  return filtered;
                }}
                getOptionLabel={(option) => {
                  if (!option) return '';
                  if (typeof option === 'string') return option;
                  // Synthetic "Add" object
                  return option.inputValue || option.label || '';
                }}
                isOptionEqualToValue={(option, val) => {
                  if (!option || !val) return false;
                  const optStr = typeof option === 'string' ? option : (option.inputValue || option.label || '');
                  const valStr = typeof val === 'string' ? val : (val.inputValue || val.label || '');
                  return optStr.toLowerCase() === valStr.toLowerCase();
                }}
                renderOption={(props, option) => {
                  if (option && typeof option === 'object' && option.inputValue) {
                    return (
                      <li {...props} key={`add-${option.inputValue}`}>
                        <Typography variant="body2" sx={{ color: '#6366F1', fontStyle: 'italic' }}>
                          {option.label}
                        </Typography>
                      </li>
                    );
                  }
                  return (
                    <li {...props} key={option}>
                      {option}
                    </li>
                  );
                }}
                onChange={(_, val) => {
                  if (val === null) {
                    handleCityChange(null);
                  } else if (typeof val === 'string') {
                    // Free-typed via Enter
                    handleCityChange(val.trim() || null);
                  } else if (val && typeof val === 'object' && val.inputValue) {
                    // Synthetic "Add" option
                    handleCityChange(val.inputValue.trim() || null);
                  } else {
                    // Existing INDIAN_CITIES string option (shouldn't be an object, but guard)
                    handleCityChange(val);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select city"
                    placeholder="Type to search or add..."
                    sx={inputSx}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <LocationOn sx={{ color: labelColor }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{
                  '& .MuiAutocomplete-paper': { ...g.surface },
                  '& .MuiAutocomplete-listbox': { color: textColor },
                }}
              />
            </Box>

            {/* Area (locality) picker — free-solo, disabled until city chosen */}
            <Box mb={3}>
              {localitiesLoading ? (
                <Box
                  sx={{
                    ...g.surface,
                    borderRadius: 1.5,
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    opacity: 0.7,
                  }}
                >
                  <CircularProgress size={16} />
                  <Typography variant="body2" sx={{ color: subColor }}>
                    Loading areas...
                  </Typography>
                </Box>
              ) : (
                <Autocomplete
                  freeSolo
                  options={localityOptions}
                  value={
                    // For the controlled value: try to match an existing option so the
                    // Autocomplete can display its label; fall back to the raw string so
                    // free-typed values round-trip correctly.
                    localityOptions.find((o) => o.value === selectedLocality) || selectedLocality
                  }
                  disabled={!selectedCity}
                  filterOptions={(options, state) => {
                    const input = state.inputValue.trim();
                    // Always keep the city-wide sentinel at top
                    const cityWide = options.filter((o) => typeof o === 'object' && o.value === CITY_WIDE_VALUE);
                    const rest = options.filter((o) => typeof o !== 'object' || o.value !== CITY_WIDE_VALUE);
                    const filtered = input
                      ? rest.filter((o) => {
                          const label = typeof o === 'string' ? o : (o.label || o.value || '');
                          return label.toLowerCase().includes(input.toLowerCase());
                        })
                      : rest;
                    // Inject "Add" option if no exact match exists (excluding city-wide)
                    const hasExact = filtered.some((o) => {
                      const label = typeof o === 'string' ? o : (o.label || o.value || '');
                      return label.toLowerCase() === input.toLowerCase();
                    });
                    if (input && !hasExact) {
                      return [
                        ...cityWide,
                        { inputValue: input, label: `Add "${input}"` },
                        ...filtered,
                      ];
                    }
                    return [...cityWide, ...filtered];
                  }}
                  getOptionLabel={(o) => {
                    if (!o) return '';
                    if (typeof o === 'string') return o;
                    // Synthetic "Add" object
                    if (o.inputValue) return o.inputValue;
                    return o.label || o.value || '';
                  }}
                  isOptionEqualToValue={(option, val) => {
                    // Both could be objects or strings (freeSolo)
                    const optVal = typeof option === 'string' ? option : (option.inputValue || option.value || '');
                    const selVal = typeof val === 'string' ? val : (val.inputValue || val.value || '');
                    return optVal === selVal;
                  }}
                  renderOption={(props, option) => {
                    if (option && typeof option === 'object' && option.inputValue) {
                      return (
                        <li {...props} key={`add-loc-${option.inputValue}`}>
                          <Typography variant="body2" sx={{ color: '#6366F1', fontStyle: 'italic' }}>
                            {option.label}
                          </Typography>
                        </li>
                      );
                    }
                    const label = typeof option === 'string' ? option : (option.label || option.value || '');
                    return (
                      <li {...props} key={typeof option === 'string' ? option : option.value}>
                        {label}
                      </li>
                    );
                  }}
                  onChange={(_, val) => {
                    // val is null (cleared), an option object, synthetic Add object, or a free-typed string
                    if (val === null) {
                      handleLocalityChange(CITY_WIDE_VALUE);
                    } else if (typeof val === 'string') {
                      // Free-typed: pass the raw string; backend normalizes on save
                      handleLocalityChange(val.trim());
                    } else if (val.inputValue !== undefined) {
                      // Synthetic "Add" option
                      handleLocalityChange(val.inputValue.trim());
                    } else {
                      // Existing option selected (includes city-wide sentinel)
                      handleLocalityChange(val.value);
                    }
                  }}
                  onInputChange={(_, inputVal, reason) => {
                    // When the user clears the field entirely, reset to city-wide
                    if (reason === 'clear') {
                      handleLocalityChange(CITY_WIDE_VALUE);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Area"
                      placeholder="City-wide (all areas)"
                      sx={selectedCity ? inputSx : inputSxDisabled}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <Map
                                sx={{
                                  color: selectedCity ? '#8B5CF6' : labelColor,
                                  transition: 'color 0.15s',
                                }}
                                fontSize="small"
                              />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                      helperText={
                        !selectedCity
                          ? 'Choose a city first'
                          : 'Pick an area or type a new one (e.g. Uttam Nagar)'
                      }
                      FormHelperTextProps={{
                        sx: { color: labelColor, mt: 0.5 },
                      }}
                    />
                  )}
                  sx={{
                    '& .MuiAutocomplete-paper': { ...g.surface },
                    '& .MuiAutocomplete-listbox': { color: textColor },
                  }}
                />
              )}
            </Box>

            {!selectedCity ? (
              <EmptyState
                icon={<Star />}
                iconIndex={0}
                title="No city selected"
                description="Choose a city above to manage its featured library slots."
              />
            ) : loading ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ color: textColor, mb: 2 }}
                >
                  {slotHeading}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                  {slots.map((slot, i) => (
                    <SlotCard
                      key={i}
                      index={i}
                      slot={slot}
                      onPick={openPicker}
                      onRemove={removeSlot}
                      onMoveUp={(idx) => moveSlot(idx, -1)}
                      onMoveDown={(idx) => moveSlot(idx, 1)}
                      canMoveUp={i > 0}
                      canMoveDown={i < MAX_SLOTS - 1}
                      disabled={isBusy}
                    />
                  ))}
                </Box>

                <Divider
                  sx={{
                    mb: 2.5,
                    borderColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,42,0.07)',
                  }}
                />

                <Box display="flex" gap={1.5} flexWrap="wrap">
                  <GradientButton
                    variant="primary"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Star />}
                    onClick={() => handleSave(false)}
                    disabled={isBusy}
                  >
                    {saving ? 'Saving...' : 'Save featured slots'}
                  </GradientButton>

                  {hasAnySlot && (
                    <GradientButton
                      variant="danger"
                      startIcon={<Delete />}
                      onClick={() => setConfirmClear(true)}
                      disabled={isBusy}
                    >
                      {clearing ? 'Clearing...' : 'Clear all slots'}
                    </GradientButton>
                  )}
                </Box>

                {/* ── Connected marketing page card ── */}
                <GlassPaper sx={{ p: 2.5, mt: 3 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: textColor, mb: 0.5 }}>
                    Connected marketing page
                  </Typography>
                  <Typography variant="caption" sx={{ color: subColor, display: 'block', mb: 2 }}>
                    Link this featured set to an SEO landing page (/library-in-…). The page shows these libraries as a carousel at the top.
                  </Typography>

                  {/* Currently linked page */}
                  {linkedPage && (
                    <Box
                      sx={{
                        ...g.surface,
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap sx={{ color: textColor }}>
                          {linkedPage.location_name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: subColor }}>
                          /library-in-{linkedPage.slug}
                        </Typography>
                      </Box>
                      <GradientButton
                        variant="ghost"
                        startIcon={
                          linkingPageId === linkedPage.id
                            ? <CircularProgress size={14} color="inherit" />
                            : <Delete />
                        }
                        onClick={() => handleUnlinkPage(linkedPage.id)}
                        disabled={linkingPageId !== null}
                        sx={{ flexShrink: 0 }}
                      >
                        Unlink
                      </GradientButton>
                    </Box>
                  )}

                  {/* Page picker + Link button */}
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <Autocomplete
                      sx={{
                        flex: 1,
                        minWidth: 200,
                        '& .MuiAutocomplete-paper': { ...g.surface },
                        '& .MuiAutocomplete-listbox': { color: textColor },
                      }}
                      options={pages}
                      value={pickedPage}
                      loading={pagesLoading}
                      getOptionLabel={(p) =>
                        p ? `${p.location_name} (/library-in-${p.slug})` : ''
                      }
                      isOptionEqualToValue={(a, b) => a?.id === b?.id}
                      onChange={(_, val) => setPickedPage(val)}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Box>
                            <Typography variant="body2" fontWeight={600} sx={{ color: textColor }}>
                              {option.location_name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: subColor }}>
                              /library-in-{option.slug}
                              {option.featured_city && (
                                <> &nbsp;·&nbsp; linked to {option.featured_city}{option.featured_locality ? ` / ${option.featured_locality}` : ''}</>
                              )}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Pick a published page"
                          size="small"
                          sx={inputSx}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {pagesLoading ? <CircularProgress size={14} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    <GradientButton
                      variant="primary"
                      onClick={handleLinkPage}
                      disabled={!pickedPage || linkingPageId !== null}
                      startIcon={
                        linkingPageId && linkingPageId !== linkedPage?.id
                          ? <CircularProgress size={14} color="inherit" />
                          : null
                      }
                      sx={{ flexShrink: 0, alignSelf: 'center' }}
                    >
                      Link page
                    </GradientButton>
                  </Box>

                  {/* Hint when picked page already has a different link */}
                  {pickedPage && pickedPage.featured_city && !(
                    sameCity(pickedPage.featured_city, selectedCity) &&
                    normalizeLocality(pickedPage.featured_locality) === normalizeLocality(selectedLocality)
                  ) && (
                    <Typography variant="caption" sx={{ color: labelColor, display: 'block', mt: 1 }}>
                      This page is currently linked to {pickedPage.featured_city}
                      {pickedPage.featured_locality ? ` / ${pickedPage.featured_locality}` : ''}.
                      Linking will re-point it here.
                    </Typography>
                  )}
                </GlassPaper>
              </>
            )}
          </GlassPaper>
        </Grid>
      </Grid>

      {/* ── Library picker dialog ── */}
      <GlassDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title="Pick a library"
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ pt: 1 }}>
          {/* Scope toggle */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: subColor }}>
              {pickerScope === 'all' ? 'Showing all active libraries' : `Showing libraries in ${selectedCity}`}
            </Typography>
            <ToggleButtonGroup
              value={pickerScope}
              exclusive
              onChange={handlePickerScopeChange}
              size="small"
              aria-label="Library search scope"
              sx={{
                '& .MuiToggleButtonGroup-grouped': {
                  ...g.surface,
                  border: 'none',
                  color: subColor,
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                    color: '#fff',
                  },
                  '&.Mui-selected:hover': {
                    background: 'linear-gradient(135deg, #4F52D3 0%, #7C4FD4 100%)',
                  },
                },
              }}
            >
              <ToggleButton value="all">All libraries</ToggleButton>
              <ToggleButton value="city">This city only</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name or address..."
            value={searchTerm}
            onChange={handleSearchChange}
            autoFocus
            sx={{ mb: 2, ...inputSx }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {candidatesLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Search sx={{ color: labelColor }} fontSize="small" />
                  )}
                </InputAdornment>
              ),
            }}
          />

          {/* Candidate list */}
          {candidates.length === 0 ? (
            <EmptyState
              icon={<Business />}
              iconIndex={1}
              title="No libraries found"
              description="Try a different search term, or check that the city has active libraries."
            />
          ) : (
            <List dense sx={{ maxHeight: 360, overflowY: 'auto' }}>
              {candidates.map((lib) => {
                const alreadyAssigned = assignedIds.has(lib.id);
                return (
                  <ListItem key={lib.id} disablePadding>
                    <ListItemButton
                      onClick={() => !alreadyAssigned && assignLibrary(lib)}
                      disabled={alreadyAssigned}
                      sx={{ borderRadius: 1.5, mb: 0.5 }}
                      aria-disabled={alreadyAssigned}
                    >
                      <Business
                        fontSize="small"
                        sx={{
                          mr: 1.5,
                          flexShrink: 0,
                          color: alreadyAssigned ? labelColor : '#6366F1',
                        }}
                      />
                      <ListItemText
                        primary={lib.name}
                        secondary={
                          [lib.city, lib.locality].filter(Boolean).join(' · ') +
                          (lib.address ? ` — ${lib.address}` : '')
                        }
                        primaryTypographyProps={{
                          variant: 'body2',
                          fontWeight: 600,
                          sx: { color: alreadyAssigned ? labelColor : textColor },
                        }}
                        secondaryTypographyProps={{
                          variant: 'caption',
                          sx: { color: subColor },
                        }}
                      />
                      {alreadyAssigned && (
                        <Typography
                          variant="caption"
                          sx={{ ml: 1, color: labelColor, flexShrink: 0 }}
                        >
                          Already in a slot
                        </Typography>
                      )}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <GradientButton variant="ghost" onClick={() => setPickerOpen(false)}>
            Cancel
          </GradientButton>
        </DialogActions>
      </GlassDialog>

      {/* ── City mismatch confirm dialog ── */}
      <GlassDialog
        open={confirmCityMismatch}
        onClose={() => setConfirmCityMismatch(false)}
        title="City mismatch"
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="warning" sx={{ mb: 1 }}>
            One or more selected libraries belong to{' '}
            <strong>{mismatchCity}</strong>, not{' '}
            <strong>{selectedCity}</strong>. Save anyway?
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <GradientButton
            variant="ghost"
            onClick={() => setConfirmCityMismatch(false)}
            disabled={saving}
          >
            Cancel
          </GradientButton>
          <GradientButton
            variant="primary"
            onClick={handleForceConfirm}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save anyway'}
          </GradientButton>
        </DialogActions>
      </GlassDialog>

      {/* ── Clear confirm dialog ── */}
      <GlassDialog
        open={confirmClear}
        onClose={() => setConfirmClear(false)}
        title="Clear featured slots"
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="warning">
            This will remove all featured library pins for{' '}
            <strong>{selectedCity}</strong>
            {selectedLocality ? (
              <>
                {' '}in{' '}
                <strong>
                  {localities.find((l) => l.value === selectedLocality)?.label || selectedLocality}
                </strong>
              </>
            ) : (
              ' (city-wide)'
            )}
            . This cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <GradientButton
            variant="ghost"
            onClick={() => setConfirmClear(false)}
            disabled={clearing}
          >
            Cancel
          </GradientButton>
          <GradientButton
            variant="danger"
            onClick={handleClear}
            disabled={clearing}
          >
            {clearing ? 'Clearing...' : 'Clear all slots'}
          </GradientButton>
        </DialogActions>
      </GlassDialog>
    </Box>
  );
}
